import router from '@/router'
import { reportStoreError } from '@/store/reportStoreError'
import { createProject, deleteProject, fetchProject, fetchProjects, renameProject, updateAnswers, generateBrief } from '@/services/projectApi'
import {
  fetchStockPlan,
  rematchProject,
  pickStockItem as pickStockItemApi,
  addMaterialLines as addMaterialLinesApi,
  deleteMaterialLine as deleteMaterialLineApi,
  logMaterialEvents as logMaterialEventsApi,
} from '@/services/stockApi'

let _debounceTimer = null

export const project = {
  namespaced: true,

  state: () => ({
    projects: [],
    currentProject: null,
    sending: false,
    // undefined = not loaded yet, null = no stock plan for this user, else summary
    stockPlan: undefined,
    // Bumped whenever the server re-resolves materials, so MaterialTable re-initialises.
    stockMatchSeq: 0,
  }),

  getters: {
    questions: state => state.currentProject?.project_data?.project?.questions ?? [],
    answers: state => state.currentProject?.answers ?? {},
    status: state => state.currentProject?.status ?? null,
    brief: state => state.currentProject?.brief ?? null,
    domain: state => state.currentProject?.domain ?? '',
    agentSessionId: state => state.currentProject?.agent_session_id ?? null,
    initialMessage: state => state.currentProject?.initial_message ?? '',
    // The project's pinned plan (métier-specific) wins over the generic lookup.
    stockPlan: state => state.currentProject?.stock_plan || state.stockPlan,
    canGenerateBrief(state) {
      return !!state.currentProject
    },
  },

  mutations: {
    SET_PROJECTS(state, projects) {
      state.projects = projects
    },
    ADD_PROJECT(state, project) {
      state.projects.unshift(project)
    },
    UPDATE_PROJECT(state, { id, title }) {
      const idx = state.projects.findIndex(p => p.id === id)
      if (idx !== -1) state.projects.splice(idx, 1, { ...state.projects[idx], title })
    },
    REMOVE_PROJECT(state, id) {
      state.projects = state.projects.filter(p => p.id !== id)
    },
    SET_PROJECT(state, project) {
      state.currentProject = project
    },
    SET_SENDING(state, val) {
      state.sending = val
    },
    MERGE_ANSWERS(state, delta) {
      if (!state.currentProject) return
      state.currentProject = {
        ...state.currentProject,
        answers: { ...state.currentProject.answers, ...delta },
      }
    },
    APPEND_BRIEF(state, text) {
      if (!state.currentProject) return
      const current = typeof state.currentProject.brief === 'string'
        ? state.currentProject.brief
        : ''
      state.currentProject = {
        ...state.currentProject,
        brief: current + text,
        status: 'generating',
      }
    },
    SET_BRIEF(state, brief) {
      if (!state.currentProject) return
      state.currentProject = { ...state.currentProject, brief }
    },
    SET_STATUS(state, status) {
      if (!state.currentProject) return
      state.currentProject = { ...state.currentProject, status }
    },
    SET_MATERIAL(state, material) {
      if (!state.currentProject || typeof state.currentProject.brief !== 'object') return
      state.currentProject = {
        ...state.currentProject,
        brief: { ...state.currentProject.brief, material },
      }
    },
    SET_STOCK_PLAN(state, plan) {
      state.stockPlan = plan
    },
    // Apply a stock_match payload ({plan, material, options}) to the current brief.
    APPLY_STOCK_MATCH(state, payload) {
      state.stockPlan = payload.plan ?? null
      // The server re-pinned the project on this plan (or cleared the pin).
      if (state.currentProject) {
        state.currentProject = { ...state.currentProject, stock_plan: payload.plan ?? null }
      }
      if (!payload.plan || !state.currentProject || typeof state.currentProject.brief !== 'object') return
      const brief = { ...state.currentProject.brief }
      if (Array.isArray(payload.material)) brief.material = payload.material
      if (Array.isArray(payload.options) && Array.isArray(brief.options)) brief.options = payload.options
      state.currentProject = { ...state.currentProject, brief }
      state.stockMatchSeq += 1
    },
    INIT_QUALIFYING(state, { id, initialMessage }) {
      state.currentProject = {
        id,
        status: 'questioning',
        project_data: { project: { questions: [] } },
        answers: {},
        domain: '',
        title: '',
        initial_message: initialMessage,
        brief: null,
        agent_session_id: null,
        created_at: null,
      }
    },
    APPEND_QUESTION(state, question) {
      if (!state.currentProject) return
      const existing = state.currentProject.project_data?.project?.questions ?? []
      state.currentProject = {
        ...state.currentProject,
        project_data: {
          ...state.currentProject.project_data,
          project: {
            ...(state.currentProject.project_data?.project ?? {}),
            questions: [...existing, question],
          },
        },
      }
    },
    FINALIZE_QUALIFYING(state, { domain, title, projectData }) {
      if (!state.currentProject) return
      const id = state.currentProject.id
      state.currentProject = { ...state.currentProject, domain, title, project_data: projectData }
      const idx = state.projects.findIndex(p => p.id === id)
      if (idx !== -1) {
        state.projects.splice(idx, 1, { ...state.projects[idx], domain, title })
      }
    },
    CLEAR_PROJECT(state) {
      state.currentProject = null
      state.sending = false
    },
  },

  actions: {
    async loadProjects({ commit }) {
      try {
        const projects = await fetchProjects()
        commit('SET_PROJECTS', projects)
      } catch (e) {
        reportStoreError('project/loadProjects', e)
        // silent — sidebar degrades gracefully
      }
    },

    async submitQuery({ commit, dispatch }, text) {
      commit('SET_SENDING', true)
      try {
        const proj = await createProject(text)
        commit('SET_PROJECT', proj)
        commit('ADD_PROJECT', { id: proj.id, title: proj.title, domain: proj.domain, initial_message: proj.initial_message, status: proj.status, created_at: proj.created_at })
        router.push({ name: 'project_detail', params: { id: proj.id } })
      } catch (err) {
        reportStoreError('project/submitQuery', err)
        const data = err.data || {}
        if (data.error === 'domain_not_found') {
          commit('chat/SHOW_NOTIFICATION', {
            type: 'info',
            message: "Je ne suis pas en mesure de créer un projet pour cette demande, mais je peux vous aider par chat !",
          }, { root: true })
          router.push({ name: 'home' })
          dispatch('chat/sendAgentMessage', { text }, { root: true })
        } else {
          commit('chat/SHOW_NOTIFICATION', {
            type: 'error',
            message: err.message || "Impossible de créer le projet.",
          }, { root: true })
        }
      } finally {
        commit('SET_SENDING', false)
      }
    },

    async renameProject({ commit }, { id, title }) {
      try {
        await renameProject(id, title)
        commit('UPDATE_PROJECT', { id, title })
        commit('chat/SHOW_NOTIFICATION', { type: 'success', message: 'Projet renommé.' }, { root: true })
      } catch (e) {
        reportStoreError('project/renameProject', e)
        commit('chat/SHOW_NOTIFICATION', { type: 'error', message: `Erreur renommage : ${e.message}` }, { root: true })
      }
    },

    async deleteProject({ commit, state }, id) {
      try {
        await deleteProject(id)
        commit('REMOVE_PROJECT', id)
        if (state.currentProject?.id === id) {
          commit('CLEAR_PROJECT')
          router.push({ name: 'home' })
        }
        commit('chat/SHOW_NOTIFICATION', { type: 'success', message: 'Projet supprimé.' }, { root: true })
      } catch (e) {
        reportStoreError('project/deleteProject', e)
        commit('chat/SHOW_NOTIFICATION', { type: 'error', message: `Erreur suppression : ${e.message}` }, { root: true })
      }
    },

    async loadProject({ commit }, id) {
      try {
        const proj = await fetchProject(id)
        commit('SET_PROJECT', proj)
      } catch (e) {
        reportStoreError('project/loadProject', e)
        router.push({ name: 'home' })
      }
    },

    updateAnswer({ commit, state }, { key, value }) {
      commit('MERGE_ANSWERS', { [key]: value })
      clearTimeout(_debounceTimer)
      _debounceTimer = setTimeout(async () => {
        if (!state.currentProject) return
        try {
          await updateAnswers(state.currentProject.id, { [key]: value })
        } catch (e) {
          reportStoreError('project/updateAnswer', e)
          // silent — next save will retry
        }
      }, 500)
    },

    async generateBrief({ commit, state }) {
      if (!state.currentProject) return
      commit('SET_STATUS', 'generating')
      const id = state.currentProject.id
      // clear any stale brief before streaming
      commit('SET_PROJECT', { ...state.currentProject, brief: '', status: 'generating' })
      try {
        await generateBrief(id, {
          onChunk: text => commit('APPEND_BRIEF', text),
          onDone: () => {
            const raw = state.currentProject?.brief
            if (typeof raw === 'string') {
              try { commit('SET_BRIEF', JSON.parse(raw)) } catch { commit('SET_BRIEF', {}) }
            }
            commit('SET_STATUS', 'complete')
          },
          onStockMatch: payload => commit('APPLY_STOCK_MATCH', payload),
          onError: msg => {
            reportStoreError('project/generateBrief:onError', { message: msg })
            commit('chat/SHOW_NOTIFICATION', { type: 'error', message: msg }, { root: true })
          },
        })
        commit('SET_STATUS', 'complete')
      } catch (err) {
        reportStoreError('project/generateBrief', err)
        commit('chat/SHOW_NOTIFICATION', {
          type: 'error',
          message: err.message || "Erreur lors de la génération du brief.",
        }, { root: true })
        commit('SET_STATUS', 'questioning')
      }
    },

    async loadStockPlan({ commit }) {
      try {
        const data = await fetchStockPlan()
        commit('SET_STOCK_PLAN', data.plan ?? null)
      } catch (err) {
        reportStoreError('project/loadStockPlan', err)
      }
    },

    // Re-run stock matching on the persisted brief (server side).
    async rematchStock({ commit, state }) {
      if (!state.currentProject) return
      try {
        const data = await rematchProject(state.currentProject.id)
        commit('APPLY_STOCK_MATCH', data)
        if (!data.plan) commit('SET_STOCK_PLAN', null)
      } catch (err) {
        reportStoreError('project/rematchStock', err)
        commit('chat/SHOW_NOTIFICATION', {
          type: 'error',
          message: err.message || 'Erreur lors de la vérification du stock.',
        }, { root: true })
      }
    },

    // Persist an explicit stock item choice for the line at ``index`` of the
    // persisted brief. Returns the updated line (or null on error).
    async pickStockItem({ commit }, { projectId, index, stockItemId }) {
      try {
        const data = await pickStockItemApi(projectId, index, stockItemId)
        return data.line
      } catch (err) {
        reportStoreError('project/pickStockItem', err)
        commit('chat/SHOW_NOTIFICATION', {
          type: 'error',
          message: err.message || "Impossible d'enregistrer ce choix d'article.",
        }, { root: true })
        return null
      }
    },

    // Persist lines added to the material table: ``{lines: [{stock_item_id,
    // raw_quantity}]}`` or ``{option_index}``. Returns ``{added, material}``
    // or null on error.
    async addMaterialLines({ commit }, { projectId, body }) {
      try {
        const data = await addMaterialLinesApi(projectId, body)
        commit('SET_MATERIAL', data.material)
        return data
      } catch (err) {
        reportStoreError('project/addMaterialLines', err)
        commit('chat/SHOW_NOTIFICATION', {
          type: 'error',
          message: err.message || "Impossible d'ajouter la ligne.",
        }, { root: true })
        return null
      }
    },

    // Persist the removal of a material line. Returns ``{material}`` (the
    // saved table, or null when the line was already gone) or null on error.
    async removeMaterialLine({ commit }, { projectId, lineId }) {
      try {
        const data = await deleteMaterialLineApi(projectId, lineId)
        commit('SET_MATERIAL', data.material)
        return data
      } catch (err) {
        if (err.status === 404) return { material: null }
        reportStoreError('project/removeMaterialLine', err)
        commit('chat/SHOW_NOTIFICATION', {
          type: 'error',
          message: err.message || 'Impossible de supprimer la ligne.',
        }, { root: true })
        return null
      }
    },

    // Fire-and-forget audit log of local edits; never blocks or notifies.
    async logMaterialEvents(_, { projectId, events }) {
      try {
        await logMaterialEventsApi(projectId, events)
      } catch (err) {
        reportStoreError('project/logMaterialEvents', err)
      }
    },
  },
}
